import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import { captureBookingPageViewed, trackCta } from "@/lib/analytics";

const CARD_DESTINATIONS = [ROUTES.bookPresentation, ROUTES.bookExecutivePresence, ROUTES.bookCorporate, ROUTES.bookLdPartnership];
const CARD_IDS = ["presentation", "executive_presence", "corporate", "ld_partnership"];
const CARD_TARGET_ASSET_IDS = ["booking_presentation", "booking_executive_presence", "booking_corporate", "booking_ld_partnership"];

/**
 * General booking selection page (/book) — four clickable cards, no
 * calendar, no intake form. Intended journey: general site CTAs land
 * here first, service-specific CTAs skip straight to a dedicated page.
 */
export default function BookingSelect() {
  const { t, l } = useLang();
  const viewedRef = useRef(false);
  const cards = t("book.select.cards");

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    captureBookingPageViewed(null);
  }, []);

  return (
    <>
      <PageMeta titleKey="meta.book_select_title" descriptionKey="meta.book_select_description" path={ROUTES.booking} />

      <section className="relative z-10 pt-14 md:pt-20 pb-16 md:pb-20" data-testid="book-select-hero">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.28em] text-terracotta mb-5 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-terracotta" />
              {t("book.select.eyebrow")}
            </p>
            <h1 className="font-serif text-[2.2rem] sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-ink max-w-2xl" data-testid="book-select-h1">
              {t("book.select.h1")}
            </h1>
            <p className="mt-6 font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-xl" data-testid="book-select-description">
              {t("book.select.description")}
            </p>
            <p className="mt-6 font-hand text-terracotta text-lg md:text-xl" data-testid="book-select-practical">
              {t("book.select.practical")}
            </p>
            <p className="mt-1 font-body text-sm text-ink-soft" data-testid="book-select-language-line">
              {t("book.select.language_line")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 pb-20 md:pb-28" data-testid="book-select-cards">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <StaggerGroup className="grid md:grid-cols-2 gap-6 md:gap-8">
            {Array.isArray(cards) &&
              cards.map((card, i) => {
                const href = CARD_DESTINATIONS[i];
                const id = CARD_IDS[i];
                const targetAssetId = CARD_TARGET_ASSET_IDS[i];
                return (
                  <StaggerItem key={id}>
                    <Link
                      to={l(href)}
                      data-testid={`book-select-card-${id}`}
                      onClick={() => trackCta({ ctaId: `book-select-card-${id}`, location: "book_select", href, targetAssetId })}
                      className="group flex flex-col h-full border border-ink/12 bg-paper p-7 md:p-9 transition-all duration-300 hover:border-terracotta hover:shadow-[6px_12px_36px_rgba(27,27,24,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2"
                    >
                      <p className="font-body text-[11px] uppercase tracking-[0.22em] text-terracotta mb-4">{card.category}</p>
                      <h2 className="font-serif text-2xl md:text-[1.7rem] text-ink leading-snug mb-3">{card.title}</h2>
                      <p className="font-body text-ink-soft leading-relaxed mb-6 flex-1">{card.description}</p>
                      <span className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.14em] text-ink group-hover:text-terracotta transition-colors">
                        {card.action_label}
                        <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">&rarr;</span>
                      </span>
                    </Link>
                  </StaggerItem>
                );
              })}
          </StaggerGroup>
        </div>
      </section>

      <section className="relative z-10 pb-24 md:pb-32" data-testid="book-select-help">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal className="max-w-2xl border-l-2 border-terracotta pl-6">
            <h3 className="font-serif text-xl md:text-2xl text-ink mb-3">{t("book.select.help_title")}</h3>
            <p className="font-body text-ink-soft leading-relaxed mb-3">{t("book.select.help_text")}</p>
            <p className="font-body text-sm text-ink-soft">{t("book.select.help_reassurance")}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
